import { useTranslation } from 'react-i18next';

import type { UserEmailExistResult } from '@hooks/useUserEmailExistCheck';
import { useState } from 'react';
import { Button } from '../Button';
import { Avatar } from '../Avatar';

export interface ExistingPersonFoundProps {
  existingUser: UserEmailExistResult | null;
  handleLinkExistingPerson: () => void;
  handleUseDifferentEmail: () => void;
}

export const ExistingPersonFound = ({
  existingUser,
  handleLinkExistingPerson,
  handleUseDifferentEmail
}: ExistingPersonFoundProps) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[70]">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-xl max-h-[90vh] overflow-y-auto p-6 relative animate-fadeIn">
        <h2 className="text-2xl text-center font-semibold mb-4">
          {t('existingPersonFound.title')}
        </h2>
        <div>
          <div className="flex flex-col items-center space-x-4 mb-4">
            <Avatar
              src={existingUser?.profileImage || undefined}
              alt={existingUser?.name || 'Profile Image'}
              size="h-32 w-32"
            />
            <div className="!ml-0 mt-2">
              <p className="text-lg font-medium">{existingUser?.name}</p>
            </div>
          </div>
        </div>
        <p className="text-gray-600 whitespace-pre-line mb-6">
          {t('existingPersonFound.message', { name: existingUser?.name })}
        </p>
        <div className="flex justify-center space-x-4">
          <Button
            btnStyle="link"
            className="px-6 py-2 !bg-[#F9FEE9] border-[var(--na-primary-color)] text-[var(--na-primary-color)] font-medium rounded-md hover:bg-[#F9FEE9] text-sm"
            isDisabled={isLoading}
            onClick={handleUseDifferentEmail}>
            {t('existingPersonFound.useDifferentEmail')}
          </Button>
          <Button
            btnStyle="primary"
            className="px-6 rounded-lg py-2"
            isDisabled={isLoading}
            onClick={() => {
              setIsLoading(true);
              handleLinkExistingPerson();
            }}>
            {isLoading
              ? t('existingPersonFound.linking', { name: existingUser?.name })
              : t('existingPersonFound.link', { name: existingUser?.name })}
          </Button>
        </div>
      </div>
    </div>
  );
};
